import { supabase } from "../integrations/supabase/client";

export type UserProfile = {
  user_id: string;
  email: string;
  details: {
    personal: {
      username: string;
      firstName: string;
      lastName: string;
    };
  };
  created_at: string;
  updated_at: string;
};

export type CreateUserProfileData = {
  user_id: string;
  email: string;
  details: {
    personal: {
      username: string;
      firstName: string;
      lastName: string;
    };
  };
};

export class UserProfilesStateService {
  private static instance: UserProfilesStateService;
  private userProfile: UserProfile = {} as UserProfile;
  private subscribers: ((userProfile: UserProfile) => void)[] = [];

  private constructor() {
    // Initialize by fetching profiles
    this.getUserProfile();
  }

  public static getInstance(): UserProfilesStateService {
    if (!UserProfilesStateService.instance) {
      UserProfilesStateService.instance = new UserProfilesStateService();
    }
    return UserProfilesStateService.instance;
  }

  // Get a single user profile
  public async getUserProfile(): Promise<UserProfile | null> {
    try {
      // First check if user is authenticated
      const { data: { user } } = await supabase.auth.getUser();

      // If no authenticated user, return null early
      if (!user) {
        console.log("No authenticated user, skipping profile info fetch");
        return null;
      }

      // Call the Supabase RPC function to get a single profile
      const { data, error } = await supabase
        .functions.invoke("profile", { method: "GET" });

      if (error) {
        console.error("Error fetching user profile:", error);
        return null;
      }

      this.userProfile = data;
      this.notifySubscribers();

      return data || null;
    } catch (error) {
      console.error("Error fetching user profile:", error);
      return null;
    }
  }

  public async createUserProfile(user_id: string): Promise<void> {
    const { data, error } = await supabase
      .functions.invoke("profile", { method: "POST", body: { user_id } });

    if (error) {
      console.error("Error creating user profile:", error);
    }
  }

  // For compatibility with existing code
  public async refreshUserProfile(): Promise<void> {
    await this.getUserProfile;
  }

  // Subscribe to changes
  public subscribe(
    callback: (userProfile: UserProfile) => void,
  ): () => void {
    this.subscribers.push(callback);
    callback(this.userProfile);

    return () => {
      this.subscribers = this.subscribers.filter((cb) => cb !== callback);
    };
  }

  // Notify all subscribers of changes
  private notifySubscribers(): void {
    for (const callback of this.subscribers) {
      callback(this.userProfile);
    }
  }
}

export const userProfilesStateService = UserProfilesStateService.getInstance();
